import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'

import { BillType } from '../../bills/enums/billType'
import { PayableSubject } from '../../paymentReconciliationModal/types/payableSubject'
import { getSubjectBalanceSum } from '../../paymentReconciliationModal/utils/getSubjectBalanceSum'
import { calculateConvertedAmount } from '../utils'
import { usePaymentModal } from './paymentModalContext'

interface SaveSelectedSubjectAndSubjectIdOptions {
  currentSubjectId: string
  isSelected: boolean
}

interface ContextState<T extends PayableSubject> {
  convertedSubjectsAmountsSum: number
  initialSubject?: T
  isCreditNoteSelected: boolean
  isLoading?: boolean
  saveSelectedSubjectAndSubjectId: ({ currentSubjectId, isSelected }: SaveSelectedSubjectAndSubjectIdOptions) => void
  selectedSubjects: T[]
  selectedSubjectsAmountsSum: number
  selectedSubjectsIds: string[]
  setSelectedSubjects?: Dispatch<SetStateAction<T[]>>
  setSelectedSubjectsIds?: Dispatch<SetStateAction<string[]>>
  subjects?: T[]
  isCashBasis?: boolean
}

const MultiplePayableSubjectsContext = createContext<ContextState<any> | undefined>(undefined)

interface MultiplePayableSubjectsContextProps<T extends PayableSubject> {
  subjects?: T[]
  isLoading?: boolean
  initialSubject?: T
  children?: ReactNode
  isCashBasis?: boolean
}

export const MultiplePayableSubjectsContextProvider = <T extends PayableSubject>({
  subjects,
  isLoading,
  children,
  initialSubject,
  isCashBasis,
}: MultiplePayableSubjectsContextProps<T>): ReactElement => {
  const [selectedSubjects, setSelectedSubjects] = useState<T[]>([])
  const [selectedSubjectsIds, setSelectedSubjectsIds] = useState<string[]>([])

  const { exchangeRate } = usePaymentModal()
  const selectedSubjectsAmountsSum = useMemo(() => {
    return getSubjectBalanceSum(selectedSubjects)
  }, [selectedSubjects])

  const sortedSubjects = useMemo(() => {
    if (subjects) {
      return [...subjects].sort((billA, billB) => {
        return Number(billB?.id === initialSubject?.id) - Number(billA?.id === initialSubject?.id)
      })
    }
    return undefined
  }, [initialSubject?.id, subjects])

  const isCreditNoteSelected = useMemo(
    () =>
      selectedSubjects.every(
        (subject) => subject.type === BillType.CreditNote || subject.type === InvoiceType.CreditNote,
      ),
    [selectedSubjects],
  )

  const convertedSubjectsAmountsSum = calculateConvertedAmount(exchangeRate, selectedSubjectsAmountsSum)

  const saveSelectedSubjectAndSubjectId = useCallback(
    ({ currentSubjectId, isSelected }: SaveSelectedSubjectAndSubjectIdOptions) => {
      const currentSubject = subjects?.find((bill) => bill?.id === currentSubjectId)

      if (currentSubject) {
        if (isSelected) {
          setSelectedSubjects((prevState) => [...prevState, currentSubject])
          setSelectedSubjectsIds((prevState) => [...prevState, currentSubjectId])
        } else {
          setSelectedSubjects((prevState) => prevState.filter((bill) => bill !== currentSubject))
          setSelectedSubjectsIds((prevState) => prevState.filter((id) => id !== currentSubjectId))
        }
      }
    },
    [subjects],
  )

  useEffect(() => {
    if (initialSubject && initialSubject?.id) {
      setSelectedSubjects([initialSubject])
      setSelectedSubjectsIds([initialSubject.id])
    }
  }, [initialSubject])

  return (
    <MultiplePayableSubjectsContext.Provider
      value={{
        convertedSubjectsAmountsSum,
        initialSubject,
        isCreditNoteSelected,
        isLoading,
        saveSelectedSubjectAndSubjectId,
        selectedSubjects,
        selectedSubjectsAmountsSum,
        selectedSubjectsIds,
        setSelectedSubjects,
        setSelectedSubjectsIds,
        isCashBasis,
        subjects: sortedSubjects,
      }}
    >
      {children}
    </MultiplePayableSubjectsContext.Provider>
  )
}

export const useMultiplePayableSubjects = <T extends PayableSubject>() => {
  const context = useContext(MultiplePayableSubjectsContext)

  if (!context) {
    throw new Error('MultiplePayableContextProvider is missing in the module!')
  }

  return context as ContextState<T>
}
